import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tokenMissing = useMemo(() => !token || token.length < 10, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, pw);
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-15"
           style={{ backgroundImage: "url(https://images.pexels.com/photos/10341390/pexels-photo-10341390.jpeg?auto=compress&cs=tinysrgb&h=650&w=940)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#101010]/95 to-[#0a0a0a] pointer-events-none" />

      <div className="relative w-full max-w-md" data-testid="reset-password-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] text-[var(--org-accent)] mb-2">
            <span className="w-8 h-px bg-[var(--org-accent)]" /> New Password <span className="w-8 h-px bg-[var(--org-accent)]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-tight leading-none">
            Reset Password
          </h1>
          <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">Choose a new password</div>
        </div>

        {tokenMissing ? (
          <div className="border border-white/10 bg-[#1c1c1c]/90 backdrop-blur p-6 space-y-4">
            <div className="text-sm text-red-300">This reset link is missing or malformed. Please request a new one.</div>
            <Link to="/forgot-password" className="inline-flex items-center gap-2 h-11 px-4 bg-[var(--org-accent)] text-[#121212] text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]" data-testid="request-new-link">
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-[#1c1c1c]/90 backdrop-blur p-6 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5 block">New password</label>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full h-11 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
                required
                autoFocus
                autoComplete="new-password"
                minLength={6}
                data-testid="reset-password-new"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5 block">Confirm password</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="w-full h-11 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
                required
                autoComplete="new-password"
                minLength={6}
                data-testid="reset-password-confirm"
              />
            </div>

            {error && (
              <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 h-11 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              style={{ transition: "background-color 0.15s ease" }}
              data-testid="reset-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Update password
            </button>

            <Link to="/login" className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#8a8a8a] hover:text-white" data-testid="back-to-login">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
